import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  Visibility,
  PictureAsPdf,
  Search,
  Gavel,
  CheckCircle,
  Warning,
  Description,
  Refresh
} from '@mui/icons-material';
import { UseApi } from '../Hooks/UseApi';
import { UseAuthRoles } from '../Hooks/UseAuthRoles';

const RecordPacketList = () => {
  const navigate = useNavigate();
  const api = UseApi();
  const { getSubscriptionLevel, isStudioOwner } = UseAuthRoles();
  
  // Use ref to track if we've already fetched to prevent infinite loops
  const hasFetched = useRef(false);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [packets, setPackets] = useState([]);
  const [filteredPackets, setFilteredPackets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const subscriptionLevel = getSubscriptionLevel();
  const canAccess = subscriptionLevel === 'growth_studio' && isStudioOwner();
  
  // Fetch all record packets - only once on mount
  useEffect(() => {
    // Skip if already fetched or no access
    if (hasFetched.current) return;
    
    if (!canAccess) {
      setError('Access denied. Record Packets are only available for Growth Studio owners.');
      setLoading(false);
      return;
    }
    
    const fetchPackets = async () => {
      hasFetched.current = true;
      setLoading(true);
      setError(null);
      
      try {
        const data = await api.fetchWithAuth('/record-packets/');
        setPackets(data.packets || []);
        setFilteredPackets(data.packets || []);
      } catch (err) {
        console.error('Error fetching record packets:', err);
        setError(err.message || 'Failed to load record packets');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPackets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - run once on mount
  
  // Manual refresh handler
  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await api.fetchWithAuth('/record-packets/');
      setPackets(data.packets || []);
      setFilteredPackets(data.packets || []);
    } catch (err) {
      console.error('Error fetching record packets:', err);
      setError(err.message || 'Failed to load record packets');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter packets based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredPackets(packets);
      return;
    }
    
    const searchLower = searchTerm.toLowerCase();
    const filtered = packets.filter(packet => {
      const clientName = `${packet.client?.first_name || ''} ${packet.client?.last_name || ''}`.toLowerCase();
      const date = packet.appointment?.date || '';
      const recordId = packet.appointment?.record_packet_id || '';
      
      return (
        clientName.includes(searchLower) ||
        date.includes(searchLower) ||
        recordId.toLowerCase().includes(searchLower)
      );
    });
    
    setFilteredPackets(filtered);
    setPage(0);
  }, [searchTerm, packets]);
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Get summary stats
  const getStats = () => {
    const total = packets.length;
    const withConsent = packets.filter(p => 
      (p.consent_form_count && p.consent_form_count > 0)
    ).length;
    const confirmed = packets.filter(p => p.appointment?.confirmed).length;
    
    return { total, withConsent, confirmed };
  };
  
  const stats = getStats();
  
  // Access denied state
  if (!canAccess) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Warning sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Access Restricted
          </Typography>
          <Typography color="text.secondary" paragraph>
            Record Packets are only available for Growth Studio owners.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/appointments')}
          >
            Back to Appointments
          </Button>
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              <Gavel sx={{ mr: 1, verticalAlign: 'bottom' }} />
              Record Packets
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Complete liability documentation for past appointments
            </Typography>
          </Box>
          
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
        
        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="primary">
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Appointments
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="success.main">
                  {stats.withConsent}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  With Consent Forms
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="info.main">
                  {stats.confirmed}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Client Confirmed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {/* Search */}
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search by client name, date, or record ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
          />
        </Paper>
        
        {/* Table */}
        <Paper elevation={2}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredPackets.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Description sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {searchTerm ? 'No matching record packets found' : 'No record packets available'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Record packets are created automatically for past appointments.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Client</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Record ID</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Consent Forms</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPackets
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((packet) => {
                        const consentCount = packet.consent_form_count || 0;
                        
                        return (
                          <TableRow 
                            key={packet.appointment?.id}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => navigate(`/appointments/${packet.appointment?.id}/record-packet`)}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {packet.client?.first_name} {packet.client?.last_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {packet.client?.email}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {formatDate(packet.appointment?.date)}
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="caption" 
                                sx={{ fontFamily: 'monospace' }}
                              >
                                {packet.appointment?.record_packet_id?.substring(0, 8)}...
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {packet.appointment?.is_walk_in ? (
                                <Chip label="Walk-in" size="small" color="info" variant="outlined" />
                              ) : (
                                <Chip label="Scheduled" size="small" color="success" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell>
                              {consentCount > 0 ? (
                                <Chip 
                                  icon={<CheckCircle />}
                                  label={`${consentCount} form${consentCount > 1 ? 's' : ''}`}
                                  size="small"
                                  color="success"
                                />
                              ) : (
                                <Chip 
                                  icon={<Warning />}
                                  label="None"
                                  size="small"
                                  color="warning"
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {packet.appointment?.confirmed ? (
                                <Chip 
                                  label="Confirmed"
                                  size="small"
                                  color="success"
                                />
                              ) : (
                                <Chip 
                                  label="Unconfirmed"
                                  size="small"
                                  color="default"
                                />
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="View Record Packet">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/appointments/${packet.appointment?.id}/record-packet`);
                                  }}
                                >
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download PDF">
                                <IconButton
                                  size="small"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    // Download PDF directly
                                    try {
                                      const response = await fetch(
                                        `${process.env.REACT_APP_API_BASE_URL || 'https://api.inkedupmanager.com'}/appointments/${packet.appointment?.id}/record-packet/pdf/`,
                                        {
                                          method: 'GET',
                                          headers: {
                                            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
                                          }
                                        }
                                      );
                                      
                                      if (response.ok) {
                                        const blob = await response.blob();
                                        const url = window.URL.createObjectURL(blob);
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.setAttribute('download', `RecordPacket_${packet.appointment?.id}.pdf`);
                                        document.body.appendChild(link);
                                        link.click();
                                        link.remove();
                                        window.URL.revokeObjectURL(url);
                                      }
                                    } catch (err) {
                                      console.error('Error downloading PDF:', err);
                                    }
                                  }}
                                >
                                  <PictureAsPdf />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                component="div"
                count={filteredPackets.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default RecordPacketList;